import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { authenticateHambiUser, getFullName } from '../utils/hambiApi';
import { getToken, setToken, setRefreshToken, clearAllAuth, attemptTokenRefresh } from '../api/client';
import i18n from '../i18n/i18n';
import { isHambiDomain } from '../config/region';
import { isJwtExpired } from '../utils/jwtUtils';
import { createSessionManager } from '../utils/sessionManager';

export const HambiContext = createContext();

/**
 * Hambi app-аас ирсэн эсэхийг шалгах
 * Hambi: ?token=xxx&locale=ru
 * Toki: ?token=xxx эсвэл ?tokenid=xxx (locale байхгүй)
 */
const isFromHambiApp = () => {
    const urlParams = new URLSearchParams(window.location.search);
    // locale параметр байвал Hambi-аас ирсэн гэж үзнэ
    return urlParams.has('locale');
};

/**
 * URL-аас token авах
 * Зөвхөн locale байгаа үед л token-ийг Hambi-д ашиглана
 */
const getHambiTokenFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);

    // locale параметр байвал Hambi token
    if (isFromHambiApp()) {
        return urlParams.get('token');
    }

    // Hambi домэйн (ajob.uz, ajob.kz) дээр locale байхгүй ч token байвал Hambi token
    if (isHambiDomain()) {
        return urlParams.get('token');
    }

    return null;
};

/**
 * URL-аас хэл авах (locale параметр)
 * Дэмжигдэх хэлүүд: ru, uz, en - бүгд ru руу map-лагдана
 */
const getHambiLangFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const locale = urlParams.get('locale') || 'uz';
    if (['uz', 'ru', 'mn'].includes(locale)) return locale;
    return 'uz'; // Default for Hambi (Uzbekistan)
};

// Session manager for Hambi (shared utility)
const hambiSession = createSessionManager('hambi_session');

/**
 * Detect if currently running inside Hambi app.
 * Only true when URL has ?locale param (current visit from Hambi).
 * A stored hambi_session does NOT mean we're in the Hambi app —
 * it just means we have credentials to try restoring. If restore fails,
 * the login modal must be shown, which requires isInHambiApp = false.
 */
const detectHambiApp = () => {
    return isFromHambiApp();
};

/**
 * HambiProvider Component
 */
export const HambiProvider = ({ children }) => {
    // Hambi user state
    const [user, setUser] = useState(null);
    const [userToken, setUserToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Language
    const [language, setLanguage] = useState('ru');

    // Platform detection - initialize synchronously so it's correct from first render
    const [isInHambiApp, setIsInHambiApp] = useState(() => detectHambiApp());

    // Whether user came from Hambi (URL token or stored session) — used for auth decisions
    // Separate from isInHambiApp (URL locale detection for UI layout)
    const [isHambiUser, setIsHambiUser] = useState(() => !!hambiSession.get());

    // Store URL token for retry attempts
    const urlTokenRef = useRef(null);

    /**
     * Хэрэглэгчийн мэдээлэл авах ба JWT token авах
     * Uses /v1/hambi/auth endpoint which validates token and issues JWT
     */
    const fetchUserInfo = useCallback(async (token) => {
        if (!token) {
            setIsLoading(false);
            return null;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Use authenticateHambiUser which returns both user data and JWT token
            const result = await authenticateHambiUser(token);

            if (result.success) {
                const userData = {
                    ...result.data,
                    fullName: getFullName(result.data)
                };
                setUser(userData);
                setUserToken(token);

                // Store JWT tokens for hrSystemServer access (same as Toki/Phone OTP)
                if (result.token) {
                    setToken(result.token);
                }
                if (result.refreshToken) {
                    setRefreshToken(result.refreshToken);
                }

                // Save session to localStorage
                hambiSession.save(userData, token);

                setIsLoading(false);
                return userData;
            } else {
                console.error('Hambi API error:', result.error);
                setError(result.error);
                setIsLoading(false);
                return null;
            }
        } catch (err) {
            console.error('Hambi fetch error:', err);
            setError(err.message || 'Холболтын алдаа');
            setIsLoading(false);
            return null;
        }
    }, []);

    /**
     * Logout функц
     */
    const logout = useCallback(() => {
        setUser(null);
        setUserToken(null);
        setIsHambiUser(false);
        clearAllAuth(); // Clears all tokens, sessions, and user data
    }, []);

    /**
     * Hambi re-authentication
     * JWT дууссан эсвэл алдаа гарсан үед Hambi token-оор дахин нэвтрэх
     * Returns true if re-auth succeeded, false otherwise
     */
    const retryAuth = useCallback(async () => {
        // Try URL token first (freshest), then stored session, then in-memory token
        // In-memory fallback is needed because clearAllAuth() may have cleared localStorage
        const storedSession = hambiSession.get();
        const retryToken = urlTokenRef.current || storedSession?.token || userToken;

        if (!retryToken) {
            return false;
        }

        const result = await fetchUserInfo(retryToken);
        return !!result;
    }, [fetchUserInfo, userToken]);

    /**
     * Initialize - App эхлэхэд ажиллана
     */
    useEffect(() => {
        const init = async () => {
            // Get language from URL
            const lang = getHambiLangFromUrl();
            setLanguage(lang);

            // URL-аас token авах
            const urlToken = getHambiTokenFromUrl();

            if (urlToken) {
                // Hambi app-аас ирсэн
                setIsInHambiApp(true);
                setIsHambiUser(true);
                // Store URL token for retry attempts
                urlTokenRef.current = urlToken;

                // Change i18n language based on locale param
                if (lang !== i18n.language) {

                    i18n.changeLanguage(lang);
                }

                await fetchUserInfo(urlToken);
            } else {
                // URL-д token байхгүй бол localStorage-аас сэргээнэ
                const storedSession = hambiSession.get();
                if (storedSession) {
                    setIsHambiUser(true);

                    // Restore user from stored session immediately (shows name/avatar while JWT refreshes)
                    setUser(storedSession.user);
                    setUserToken(storedSession.token);

                    // Check if existing JWT is still valid
                    const existingJwt = getToken();
                    const jwtValid = existingJwt && !isJwtExpired(existingJwt);

                    const refreshJwt = async () => {
                        // Try Hambi API first, fall back to standard /auth/refresh
                        try {
                            if (storedSession.token) {
                                const result = await authenticateHambiUser(storedSession.token);
                                if (result.success && result.token) {
                                    setToken(result.token);
                                    if (result.refreshToken) setRefreshToken(result.refreshToken);
                                    return;
                                }
                            }
                        } catch (err) {
                            console.warn('Hambi API refresh failed, trying standard refresh...');
                        }
                        // Fallback: use /auth/refresh with the long-lived refresh token (365 days)
                        await attemptTokenRefresh();
                    };

                    if (jwtValid) {
                        // JWT still valid — ready immediately, refresh in background
                        setIsLoading(false);
                        refreshJwt();
                    } else {
                        // JWT expired — must wait for refresh before components can fetch
                        refreshJwt().finally(() => setIsLoading(false));
                    }
                } else {
                    setIsLoading(false);
                }
            }
        };

        init();
    }, [fetchUserInfo]);

    // Clear React state when session is fully expired (tokens cleared by client.js)
    useEffect(() => {
        const handleSessionExpired = () => {
            setUser(null);
            setUserToken(null);
            setIsHambiUser(false);
        };
        window.addEventListener('auth:sessionExpired', handleSessionExpired);
        return () => window.removeEventListener('auth:sessionExpired', handleSessionExpired);
    }, []);

    // Context value
    const value = {
        // User info
        user,
        userToken,
        isLoading,
        error,

        // Language
        language,

        // Platform info
        isInHambiApp,
        isHambiUser,

        // Auth state
        isAuthenticated: !!user,

        // Functions
        fetchUserInfo,
        retryAuth,
        logout
    };

    return (
        <HambiContext.Provider value={value}>
            {children}
        </HambiContext.Provider>
    );
};

/**
 * useHambi Hook
 */
export const useHambi = () => {
    const context = useContext(HambiContext);

    if (!context) {
        throw new Error('useHambi must be used within a HambiProvider');
    }

    return context;
};

export default HambiContext;
